import { cn } from '@/lib/utils';
import { endpoints } from '@/services/endpoints';
import { axiosGetRequest } from '@/services/Request';
import { IChatMessage, ITicketData } from '@/types';
import { useQuery } from '@tanstack/react-query';
import ChatTicketSkeleton from '../TicketSkeletons/ChatTicketSkeleton';
import Moment from 'moment';
import {
  AlertTriangle,
  Clock,
  Hash,
  MessageSquare,
  Shield,
  User,
  Users,
} from 'lucide-react';

interface ChatParticipant {
  _id: string;
  fullName: string;
  gender: string;
}

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200',
};

export default function ReportedChat({ ticket }: { ticket: ITicketData }) {
  const {
    reporter_user,
    chat_id,
    reason,
    comment,
    ticket_code,
    ticket_status,
    admin_feedback,
    createdAt,
  } = ticket;

  const { data: chatData, isLoading } = useQuery({
    queryKey: [endpoints.getUserChats, chat_id],
    queryFn: () => axiosGetRequest(endpoints.getUserChats, { chatId: chat_id }),
  });

  const messages: IChatMessage[] = chatData?.result || [];
  const participants: ChatParticipant[] = chatData?.participants || [];
  const pagination = chatData?.pagination;

  if (isLoading) {
    return <ChatTicketSkeleton />;
  }

  return (
    <div className="flex h-full flex-col">
      {/* Report Info + Participants header */}
      <div className="space-y-3 border-b border-coal-200 bg-white p-4">
        {/* Report Details */}
        <div className="rounded-lg border border-coal-200 bg-coal-50/50 p-3.5">
          <div className="mb-2.5 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-semibold text-coal-800">Report Details</span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
            <div className="flex items-center gap-1.5">
              <Hash className="h-3.5 w-3.5 text-coal-400" />
              <span className="text-xs text-coal-400">Code</span>
              <span className="text-xs font-medium text-coal-700">{ticket_code}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Shield className="h-3.5 w-3.5 text-coal-400" />
              <span className="text-xs text-coal-400">Reason</span>
              <span className="text-xs font-medium capitalize text-coal-700">{reason}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 text-coal-400" />
              <span className="text-xs text-coal-400">Filed</span>
              <span className="text-xs font-medium text-coal-700">
                {Moment(createdAt).fromNow()}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span
                className={cn(
                  'rounded-md border px-2 py-0.5 text-xs font-medium capitalize',
                  STATUS_STYLES[ticket_status] || 'bg-coal-50 text-coal-600 border-coal-200'
                )}
              >
                {ticket_status}
              </span>
            </div>
          </div>

          {/* Comment from reporter */}
          {comment && (
            <div className="mt-2.5 rounded-md border border-coal-200 bg-white p-2.5">
              <div className="mb-1 flex items-center gap-1.5">
                <User className="h-3 w-3 text-coal-400" />
                <span className="text-xs font-medium text-coal-600">
                  {reporter_user?.user_name || 'Reporter'}
                </span>
              </div>
              <p className="text-sm text-coal-700">"{comment}"</p>
            </div>
          )}

          {/* Admin feedback */}
          {admin_feedback && (
            <div className="mt-2 rounded-md border border-indigo-100 bg-indigo-50/50 p-2.5">
              <div className="mb-1 flex items-center gap-1.5">
                <Shield className="h-3 w-3 text-indigo-500" />
                <span className="text-xs font-medium text-indigo-600">Admin Feedback</span>
              </div>
              <p className="text-sm text-indigo-700">{admin_feedback}</p>
            </div>
          )}
        </div>

        {/* Participants */}
        {participants.length > 0 && (
          <div className="rounded-lg border border-coal-200 bg-coal-50/50 p-3.5">
            <div className="mb-2 flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-coal-800">
                Participants ({participants.length})
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {participants.map((p) => {
                const isReporter = p._id === reporter_user?.reporter_id;
                return (
                  <div
                    key={p._id}
                    className={cn(
                      'flex items-center gap-2 rounded-lg border px-3 py-2 transition-colors',
                      isReporter
                        ? 'border-amber-200 bg-amber-50'
                        : 'border-coal-200 bg-white'
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white',
                        isReporter
                          ? 'bg-amber-400'
                          : p.gender === 'female'
                            ? 'bg-pink-400'
                            : 'bg-blue-400'
                      )}
                    >
                      {p.fullName?.[0]?.toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-coal-800">{p.fullName}</p>
                      <p className="text-2xs text-coal-400">
                        {isReporter ? 'Reporter' : 'Participant'}
                        {p.gender ? ` • ${p.gender}` : ''}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Messages count */}
      <div className="flex items-center gap-2 border-b border-coal-200 bg-white px-4 py-2">
        <MessageSquare className="h-3.5 w-3.5 text-coal-400" />
        <span className="text-xs font-medium text-coal-500">
          {pagination?.totalMessages || messages.length} messages
        </span>
      </div>

      {/* Chat messages */}
      <div className="flex flex-1 flex-col gap-2 overflow-auto bg-coal-100/80 p-4">
        {messages.length > 0 ? (
          messages.map((msg: IChatMessage, index: number) => {
            const isReporter = msg.senderId === reporter_user?.reporter_id;
            const senderName = msg.sender?.fullName || 'Unknown';
            // Show date separator if day changes
            const prevMsg = index > 0 ? messages[index - 1] : null;
            const showDateSeparator =
              !prevMsg ||
              Moment(msg.timestamp).format('YYYY-MM-DD') !==
                Moment(prevMsg.timestamp).format('YYYY-MM-DD');

            return (
              <div key={msg._id}>
                {showDateSeparator && (
                  <div className="my-2 flex items-center gap-3">
                    <div className="h-px flex-1 bg-coal-200" />
                    <span className="text-2xs font-medium text-coal-400">
                      {Moment(msg.timestamp).format('MMM DD, YYYY')}
                    </span>
                    <div className="h-px flex-1 bg-coal-200" />
                  </div>
                )}
                <div
                  className={cn(
                    'flex',
                    isReporter ? 'justify-start' : 'justify-end'
                  )}
                >
                  <div className="max-w-[80%]">
                    {/* Sender name */}
                    <p
                      className={cn(
                        'mb-0.5 text-2xs font-medium',
                        isReporter ? 'text-amber-600' : 'text-blue-600',
                        isReporter ? 'text-left' : 'text-right'
                      )}
                    >
                      {senderName}
                      {isReporter && (
                        <span className="ml-1 text-amber-400">(reporter)</span>
                      )}
                    </p>
                    <div
                      className={cn(
                        'break-words rounded-xl px-3 py-2 text-sm shadow-sm',
                        isReporter
                          ? 'rounded-tl-sm bg-white text-coal-800'
                          : 'rounded-tr-sm bg-primary text-white'
                      )}
                    >
                      {msg.content}
                    </div>
                    <span
                      className={cn(
                        'mt-0.5 block text-2xs text-coal-400',
                        isReporter ? 'text-left' : 'text-right'
                      )}
                    >
                      {Moment(msg.timestamp).format('h:mm A')}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="flex h-full flex-col items-center justify-center">
            <MessageSquare className="mb-2 h-8 w-8 text-coal-300" />
            <p className="text-sm text-coal-400">No messages in this chat</p>
          </div>
        )}
      </div>
    </div>
  );
}
